import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface GalleryDioramaArt {
  img?: string
  ratio?: number
  /** No print hung at this position yet — renders a dashed placeholder instead of a photo. */
  ghost?: boolean
}

export interface GalleryDioramaProps extends Omit<BoxProps, 'children'> {
  /** Room wall tint, hex — pass the room's real `wallColor` once known. */
  wallColor: string
  /** Room floor tint, hex — pass the room's real `floorColor` once known. */
  floorColor: string
  /** Up to 3 hung prints, left-to-right. Empty renders two ghost placeholders. */
  arts?: GalleryDioramaArt[]
  /** `sm` drops the spotlight glow and lightens shadows for compact list thumbnails. */
  size?: 'lg' | 'sm'
  /** Lighter rooms (e.g. Pavilion) need a darker ghost-frame outline to stay visible. */
  light?: boolean
}

// Evenly spaced center positions (%) for 1–3 wall-mounted frames — mirrors
// the mockup prototype's SPREAD table exactly.
const SPREAD: Record<number, number[]> = { 1: [50], 2: [30, 70], 3: [18, 50, 82] }

function shade(hex: string, amt: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return hex
  const clamp = (v: number) => Math.max(0, Math.min(255, v))
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => clamp(parseInt(h, 16) + amt))
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

const Root = styled(Box, { shouldForwardProp: (p) => p !== 'floorColor' })<{ floorColor: string }>(
  ({ floorColor }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: floorColor,
    // Frames lift and spots brighten whenever the room itself is hovered —
    // works whether that's a direct hover or bubbled from a parent card,
    // since the pointer is physically over this box either way.
    '&:hover .ds-dio-frame': {
      transform: 'translateX(-50%) translateY(-3px)',
      boxShadow: '0 4px 5px rgba(0,0,0,0.28), 0 16px 30px rgba(0,0,0,0.5)',
    },
    '&:hover .ds-dio-spot': { opacity: 0.95, animationPlayState: 'paused' },
  }),
)

const Ceil = styled(Box, { shouldForwardProp: (p) => p !== 'ceil' && p !== 'wall' })<{ ceil: string; wall: string }>(
  ({ ceil, wall }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '18%',
    background: `linear-gradient(180deg, ${ceil}, ${wall})`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, rgba(0,0,0,0.28), transparent 20%, transparent 80%, rgba(0,0,0,0.28))',
    },
  }),
)

const Wall = styled(Box, { shouldForwardProp: (p) => p !== 'wall' && p !== 'wallEdge' })<{
  wall: string
  wallEdge: string
}>(({ wall, wallEdge }) => ({
  position: 'absolute',
  top: '18%',
  left: 0,
  right: 0,
  height: '54%',
  overflow: 'hidden',
  background: [
    'linear-gradient(90deg, rgba(0,0,0,0.34) 0%, transparent 16%, transparent 84%, rgba(0,0,0,0.34) 100%)',
    `linear-gradient(180deg, ${wallEdge}, ${wall} 30%, ${wall} 100%)`,
  ].join(', '),
}))

const spotPulse = {
  '@keyframes ds-dio-spot-pulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 0.82 },
  },
}

const Spot = styled(Box, { shouldForwardProp: (p) => p !== 'x' && p !== 'light' })<{ x: number; light?: boolean }>(
  ({ x, light }) => ({
    position: 'absolute',
    top: '-22%',
    left: `${x}%`,
    width: '46%',
    height: '90%',
    transform: 'translateX(-50%)',
    background: light
      ? 'radial-gradient(ellipse 50% 55% at 50% 0%, rgba(255,255,255,0.85), rgba(255,255,255,0.15) 45%, transparent 72%)'
      : 'radial-gradient(ellipse 50% 55% at 50% 0%, rgba(255,255,255,0.5), rgba(255,255,255,0.08) 45%, transparent 72%)',
    filter: 'blur(2px)',
    opacity: 0.62,
    animation: 'ds-dio-spot-pulse 6s ease-in-out infinite',
    pointerEvents: 'none',
    ...spotPulse,
  }),
)

const Frame = styled(Box, { shouldForwardProp: (p) => p !== 'x' && p !== 'w' && p !== 'ghost' && p !== 'light' })<{
  x: number
  w: number
  ghost?: boolean
  light?: boolean
}>(({ x, w, ghost, light }) => ({
  position: 'absolute',
  bottom: '8%',
  left: `${x}%`,
  width: `${w}%`,
  aspectRatio: '4 / 5',
  transform: 'translateX(-50%)',
  borderRadius: 2,
  transition: 'transform 340ms cubic-bezier(0.16,1,0.3,1), box-shadow 340ms ease',
  ...(ghost
    ? {
        background: 'transparent',
        border: `1.5px dashed ${light ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.22)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: light ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.3)',
        fontSize: 15,
        fontWeight: 300,
      }
    : {
        background: '#fdfdfc',
        padding: '6%',
        boxSizing: 'border-box',
        boxShadow: '0 2px 3px rgba(0,0,0,0.25), 0 10px 22px rgba(0,0,0,0.42)',
      }),
}))

const FrameImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  borderRadius: 1,
})

const Floor = styled(Box, { shouldForwardProp: (p) => p !== 'floorColor' && p !== 'light' })<{
  floorColor: string
  light?: boolean
}>(({ floorColor, light }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '28%',
  background: `linear-gradient(180deg, rgba(0,0,0,${light ? 0.14 : 0.3}), transparent 22%), ${floorColor}`,
}))

/**
 * Pure-CSS "room diorama" — a one-point-perspective room corner (ceiling
 * band, tinted back wall, soft track-lighting spots, real framed prints,
 * floor) used as the `/galleries` card cover. No canvas/WebGL: just
 * gradients, shadows and up to 3 small `<img>`s, so it scales to any grid
 * size at effectively zero cost — preferred over a live Babylon preview
 * per card for that reason.
 */
export function GalleryDiorama({
  wallColor,
  floorColor,
  arts = [],
  size = 'lg',
  light,
  ...rest
}: GalleryDioramaProps) {
  const ceil = shade(wallColor, light ? 18 : 20)
  const wallEdge = shade(wallColor, light ? -12 : -22)
  const list = arts.length > 0 ? arts.slice(0, 3) : [{ ghost: true }, { ghost: true }]
  const xs = SPREAD[list.length] || SPREAD[3]
  const widths = list.length === 1 ? [30] : list.length === 2 ? [28, 22] : [26, 20, 20]

  return (
    <Root floorColor={floorColor} {...rest}>
      <Ceil ceil={ceil} wall={wallColor} />
      <Wall wall={wallColor} wallEdge={wallEdge}>
        {size === 'lg' && list.map((_, i) => <Spot key={i} className='ds-dio-spot' x={xs[i]} light={light} />)}
        {list.map((art, i) => (
          <Frame key={i} className='ds-dio-frame' x={xs[i]} w={widths[i]} ghost={art.ghost} light={light}>
            {!art.ghost && art.img && <FrameImg src={art.img} alt='' loading='lazy' />}
            {art.ghost && <span>＋</span>}
          </Frame>
        ))}
      </Wall>
      <Floor floorColor={floorColor} light={light} />
    </Root>
  )
}
